/**
 * Emergency location sharing for the caregiver view.
 *
 * A caregiver away from home — at a park, a practice, an ER waiting room —
 * needs to tell the parent exactly where they are. This resolves the browser's
 * current position into a Google Maps link the caregiver can paste into a text.
 *
 * Nothing is sent to our server and nothing is stored: the coordinates never
 * leave the caregiver's device except through their own clipboard.
 */

/** Matches the reference app's options — a real fix, not a cached city-block one. */
const POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 0,
};

/** Thrown with a message already phrased for display in a toast. */
export class GeolocationUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GeolocationUnavailableError";
  }
}

function messageForPositionError(err: GeolocationPositionError): string {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return "Location permission was denied. Allow location access in your browser to share it.";
    case err.POSITION_UNAVAILABLE:
      return "Your location could not be determined right now.";
    case err.TIMEOUT:
      return "Timed out finding your location. Please try again.";
    default:
      return "Your location could not be determined right now.";
  }
}

/**
 * Resolve the current position and format it as a Google Maps link.
 * Rejects with a `GeolocationUnavailableError` whose message is display-ready.
 */
export function getEmergencyLocationLink(): Promise<string> {
  // Geolocation needs a secure context: fine on https:// and on localhost,
  // absent on a plain-http origin.
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.reject(
      new GeolocationUnavailableError("This browser can't share your location."),
    );
  }

  return new Promise<string>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        resolve(`https://maps.google.com/maps?q=${latitude},${longitude}&z=18`);
      },
      (err) => reject(new GeolocationUnavailableError(messageForPositionError(err))),
      POSITION_OPTIONS,
    );
  });
}

/**
 * Copy text to the clipboard, reporting whether it landed there. The Clipboard
 * API is missing or blocked outside a secure context and in some in-app
 * browsers, so callers must be ready to show the text for manual copying
 * instead of assuming success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
